/* SY — página de produto: galeria em carrossel, variantes, lista suspensa, quantidade, compra e
   barra de compra fixa. Sem dependência: a compra usa o SY.adicionar do sy.js (abre a gaveta). */
(() => {
  'use strict';

  /* ---------------------------------------------------------------- galeria */
  class SyGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-sy-track]');
      if (!this.track) return;
      // galeria por cor: todos os itens ficam no documento e só o grupo da variante aparece; `slides` e
      // `thumbs` são sempre os VISÍVEIS, então as posições contam dentro do grupo
      this.todos = [...this.track.children];
      this.todasMiniaturas = [...this.querySelectorAll('.sy-gallery__thumb')];
      this.grupo = this.dataset.grupo || null;
      this.slides = this.todos.filter((s) => !s.hidden);
      this.thumbs = this.todasMiniaturas.filter((b) => !b.hidden);
      this.indice = Number(this.dataset.inicial || 0);
      if (this.indice) requestAnimationFrame(() => this.irPara(this.indice, 'instant'));
      else requestAnimationFrame(() => this.marcar(0));
      window.addEventListener('resize', () => this.ajustarAltura());
      this.todasMiniaturas.forEach((b) => b.addEventListener('click', () => {
        const i = this.thumbs.indexOf(b);
        if (i >= 0) this.irPara(i);
      }));
      let espera;
      this.track.addEventListener('scroll', () => {
        clearTimeout(espera);
        espera = setTimeout(() => this.aoRolar(), 60);
      }, { passive: true });

      const viewer = this.querySelector('.sy-gallery__viewer');
      const cursor = this.querySelector('custom-cursor');
      const computador = window.matchMedia('(pointer: fine) and (min-width: 1000px)');
      if (viewer && cursor && this.slides.length > 1) {
        viewer.addEventListener('pointermove', (e) => {
          if (e.pointerType !== 'mouse' || !computador.matches) return;
          const r = viewer.getBoundingClientRect();
          const x = e.clientX - r.left - cursor.clientWidth / 2;
          const y = e.clientY - r.top - cursor.clientHeight / 2;
          cursor.style.translate = `${x.toFixed(3)}px ${y.toFixed(3)}px`;
          cursor.classList.add('is-visible');
          cursor.classList.toggle('is-half-start', e.clientX - r.left < r.width / 2);
        });
        viewer.addEventListener('pointerleave', () => cursor.classList.remove('is-visible'));
        viewer.addEventListener('click', (e) => {
          if (!computador.matches) return;
          const r = viewer.getBoundingClientRect();
          const passo = e.clientX - r.left < r.width / 2 ? -1 : 1;
          this.irPara(Math.max(0, Math.min(this.slides.length - 1, this.indice + passo)));
        });
      } else if (cursor) {
        cursor.hidden = true;
      }
    }

    passo() {
      return this.track.clientWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
    }

    irPara(i, comportamento = 'smooth') {
      if (!this.slides[i]) return;
      this.track.scrollTo({ left: i * this.passo(), behavior: comportamento });
      this.marcar(i);
    }

    irParaMidia(id) {
      const alvo = this.todos.find((s) => s.dataset.mediaId === String(id));
      if (!alvo) return;
      if (this.hasAttribute('data-agrupar') && alvo.dataset.grupo && alvo.dataset.grupo !== 'g' && alvo.dataset.grupo !== this.grupo) {
        this.agrupar(alvo.dataset.grupo);
      }
      const i = this.slides.indexOf(alvo);
      if (i >= 0 && i !== this.indice) this.irPara(i);
    }

    agrupar(grupo) {
      this.grupo = grupo;
      this.dataset.grupo = grupo;
      const ver = (n) => n.dataset.grupo === grupo || n.dataset.grupo === 'g';
      this.todos.forEach((s) => { s.hidden = !ver(s); });
      this.todasMiniaturas.forEach((b) => { b.hidden = !ver(b); });
      this.slides = this.todos.filter((s) => !s.hidden);
      this.thumbs = this.todasMiniaturas.filter((b) => !b.hidden);
      this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.marcar(0);
    }

    aoRolar() {
      const i = Math.round(this.track.scrollLeft / Math.max(1, this.passo()));
      if (i !== this.indice && this.slides[i]) this.marcar(i);
    }

    /* altura adaptável (o adaptive-height do tema da referência): a faixa fica com a altura da foto
       escolhida, e não com a da foto mais alta do grupo */
    ajustarAltura() {
      const s = this.slides[this.indice];
      if (!s) return;
      const h = s.clientHeight;
      if (h && h !== this.track.clientHeight) this.track.style.maxHeight = `${h}px`;
    }

    marcar(i) {
      this.indice = i;
      this.ajustarAltura();
      const img = this.slides[i] && this.slides[i].querySelector('img');
      if (img && !img.complete) img.addEventListener('load', () => { if (this.slides[this.indice] && this.slides[this.indice].contains(img)) this.ajustarAltura(); }, { once: true });
      this.slides.forEach((s, k) => s.classList.toggle('is-selected', k === i));
      this.thumbs.forEach((b, k) => b.setAttribute('aria-current', String(k === i)));
      const b = this.thumbs[i];
      if (!b) return;
      const lista = b.parentElement;
      if (getComputedStyle(lista).gridAutoFlow.startsWith('row')) {
        lista.scrollTo({ top: b.offsetTop - lista.offsetTop - (lista.clientHeight - b.clientHeight) / 2, behavior: 'smooth' });
      } else {
        lista.scrollTo({ left: b.offsetLeft - lista.offsetLeft - (lista.clientWidth - b.clientWidth) / 2, behavior: 'smooth' });
      }
    }
  }

  /* ---------------------------------------------------------------- produto: variantes e compra */
  class SyProduct extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.form;
      this.form = document.getElementById(this.formId);
      const fonte = document.querySelector(`[data-sy-variantes="${this.formId}"]`);
      this.variantes = fonte ? JSON.parse(fonte.textContent) : [];
      this.picker = this.querySelector('variant-picker');
      this.galeria = this.querySelector('product-gallery');
      this.qa = document.querySelector(`product-quick-add[data-form="${this.formId}"]`);
      this.aberto = null;

      if (this.picker) this.picker.addEventListener('change', (e) => this.aoMudar(e));
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-sy-qty]');
        if (!b) return;
        const campo = b.parentElement.querySelector('input');
        campo.value = Math.max(1, (parseInt(campo.value, 10) || 1) + Number(b.dataset.syQty));
      });
      this.querySelectorAll('.sy-select').forEach((bt) => bt.addEventListener('click', () => this.abrir(bt)));
      // clique no próprio x-popover (fora da lista) é o botão redondo de fechar ou a borda da folha
      this.querySelectorAll('x-popover').forEach((x) => x.addEventListener('click', (e) => { if (e.target === x) this.fechar(); }));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.fechar(); });
      document.addEventListener('click', (e) => { if (this.aberto && !e.target.closest('.sy-select-wrap')) this.fechar(); });
      if (this.form) this.form.addEventListener('submit', (e) => this.comprar(e));
      // a oferta por quantidade (ewc-bundle) avisa a cor escolhida numa unidade: a página vai para ela
      if (this.form) this.form.addEventListener('ewc-bundle:variante', (e) => this.escolherVariante(e.detail && e.detail.id));
      this.marcarInexistentes();
    }

    escolhidas() {
      return [...this.picker.querySelectorAll('fieldset.sy-vp__option')].map((f) => {
        const r = f.querySelector('input:checked');
        return r ? r.value : null;
      });
    }

    aoMudar(e) {
      this.picker.querySelectorAll('fieldset.sy-vp__option').forEach((f) => {
        const r = f.querySelector('input:checked');
        f.querySelectorAll('[data-sy-escolha]').forEach((s) => { s.textContent = r ? r.value : ''; });
        f.querySelectorAll('.sy-listbox__option').forEach((l) => l.setAttribute('aria-selected', String(!!r && l.contains(r))));
      });
      if (e && e.target.closest && e.target.closest('x-popover')) this.fechar();
      const escolha = this.escolhidas();
      this.marcarInexistentes();
      this.aplicar(this.variantes.find((v) => v.options.every((o, i) => o === escolha[i])));
    }

    escolherVariante(id) {
      const v = this.variantes.find((x) => String(x.id) === String(id));
      if (!v) return;
      if (!this.picker) { this.aplicar(v); return; }
      const grupos = [...this.picker.querySelectorAll('fieldset.sy-vp__option')];
      v.options.forEach((o, i) => {
        const r = grupos[i] && [...grupos[i].querySelectorAll('input[type="radio"]')].find((x) => x.value === o);
        if (r) r.checked = true;
      });
      this.aoMudar();
    }

    /* valor que não forma variante com as outras escolhas aparece riscado */
    marcarInexistentes() {
      if (!this.picker) return;
      const escolha = this.escolhidas();
      this.picker.querySelectorAll('fieldset.sy-vp__option').forEach((f, p) => {
        f.querySelectorAll('input[type="radio"]').forEach((r) => {
          const existe = this.variantes.some((v) => v.options[p] === r.value
            && v.options.every((o, q) => q === p || escolha[q] === null || o === escolha[q]));
          const rotulo = r.closest('label') || r.nextElementSibling;
          if (rotulo) rotulo.classList.toggle('is-disabled', !existe);
        });
      });
    }

    botoes() {
      return document.querySelectorAll(`[data-sy-comprar][form="${this.formId}"], #${this.formId} [data-sy-comprar]`);
    }

    aplicar(v) {
      if (!v) {
        this.botoes().forEach((b) => { b.disabled = true; b.firstElementChild.textContent = 'Indisponible'; });
        return;
      }
      this.form.querySelector('[data-sy-variante]').value = v.id;
      this.botoes().forEach((b) => {
        b.disabled = !v.available;
        b.firstElementChild.textContent = v.available ? (b.dataset.syRotulo || 'Ajouter au panier') : 'Épuisé';
      });
      const precos = `<sale-price class="${v.compare ? 'sy-on-sale' : ''}"><span class="sy-sr">Prix de vente</span>${v.price}</sale-price>`
        + (v.compare ? `<compare-at-price class="sy-subdued sy-line-through"><span class="sy-sr">Prix normal</span>${v.compare}</compare-at-price>` : '');
      const bloco = this.querySelector('[data-sy-preco]');
      if (bloco) {
        bloco.innerHTML = `<price-list class="sy-price sy-price--lg">${precos}</price-list>`
          + (v.save ? `<div class="sy-pi-badges"><on-sale-badge class="sy-badge">Economisez ${v.save}</on-sale-badge></div>` : '');
      }
      if (this.qa) {
        const p = this.qa.querySelector('[data-sy-qa-preco]');
        // na barra fixa o preço sem desconto sai no tom suave, como na referência
        if (p) p.innerHTML = precos.replace('<sale-price class="">', '<sale-price class="sy-subdued">');
        const img = this.qa.querySelector('[data-sy-qa-img]');
        if (img && v.img) { img.removeAttribute('srcset'); img.src = v.img; }
      }
      if (v.media && this.galeria && this.galeria.irParaMidia) this.galeria.irParaMidia(v.media);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    abrir(bt) {
      const pop = document.getElementById(bt.getAttribute('aria-controls'));
      if (!pop) return;
      if (this.aberto === pop) { this.fechar(); return; }
      this.fechar();
      pop.hidden = false;
      bt.setAttribute('aria-expanded', 'true');
      this.aberto = pop;
      this.botaoAberto = bt;
      if (!window.matchMedia('(min-width: 1000px)').matches) document.documentElement.style.overflow = 'hidden';
    }

    fechar() {
      if (!this.aberto) return;
      this.aberto.hidden = true;
      this.botaoAberto.setAttribute('aria-expanded', 'false');
      this.aberto = null;
      document.documentElement.style.overflow = '';
    }

    comprar(e) {
      e.preventDefault();
      const id = this.form.querySelector('[data-sy-variante]').value;
      const campo = document.querySelector(`input[name="quantity"][form="${this.formId}"]`);
      const botao = e.submitter || this.form.querySelector('[data-sy-comprar]');
      if (window.SY && window.SY.adicionar) window.SY.adicionar(id, botao, campo ? campo.value : 1);
      else this.form.submit();
    }
  }

  /* ---------------------------------------------------------------- barra de compra fixa */
  class SyQuickAdd extends HTMLElement {
    connectedCallback() {
      const form = document.getElementById(this.dataset.form);
      const rodape = document.querySelector('.sy-sec--footer, .sy-footer');
      if (!form) return;
      // aparece depois que o formulário principal sai por cima da tela e some ao chegar no rodapé;
      // conferido na rolagem (um salto de rolagem não passa pelo cruzamento que um observador veria)
      let pedido = 0;
      const conferir = () => {
        pedido = 0;
        const passou = form.getBoundingClientRect().bottom < 0;
        const chegou = !!rodape && rodape.getBoundingClientRect().top < window.innerHeight;
        this.classList.toggle('is-visible', passou && !chegou);
      };
      this.aoRolar = () => { if (!pedido) pedido = requestAnimationFrame(conferir); };
      window.addEventListener('scroll', this.aoRolar, { passive: true });
      window.addEventListener('resize', this.aoRolar, { passive: true });
      conferir();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.aoRolar);
      window.removeEventListener('resize', this.aoRolar);
    }
  }

  /* ---------------------------------------------------------------- vídeos em círculo (stories) */
  // cada círculo toca 10 s sem som ao abrir a página e volta ao começo; o clique abre o vídeo
  // numa janela, com som e controles; fundo escuro ou × fecham e pausam
  const ligarStories = (raiz) => {
    if (raiz.dataset.syLigado) return;
    raiz.dataset.syLigado = '1';
    {
      const janela = raiz.querySelector('[data-sy-story-modal]');
      const video = janela && janela.querySelector('video');
      if (!janela || !video) return;
      raiz.querySelectorAll('.sy-story').forEach((botao) => botao.addEventListener('click', () => {
        video.querySelector('source').setAttribute('src', botao.dataset.video);
        video.load();
        video.play().catch(() => {});
        janela.classList.add('active');
      }));
      janela.addEventListener('click', (e) => {
        if (e.target.classList.contains('sy-story-modal__overlay') || e.target.closest('.sy-story-modal__close')) {
          video.pause();
          janela.classList.remove('active');
        }
      });
      raiz.querySelectorAll('.sy-story__mini').forEach((v) => {
        v.play().then(() => setTimeout(() => { v.pause(); v.currentTime = 0; }, 10000)).catch(() => {});
      });
    }
  };
  const iniciarStories = () => document.querySelectorAll('[data-sy-stories]').forEach(ligarStories);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciarStories); else iniciarStories();
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', SyGallery);
  if (!customElements.get('sy-product')) customElements.define('sy-product', SyProduct);
  if (!customElements.get('product-quick-add')) customElements.define('product-quick-add', SyQuickAdd);
})();
